import { API_CONFIG, API_ENDPOINTS, getHeadersWithOrg, apiCall, authFetch } from './client';
import { getAuthToken, OAUTH_CONFIG } from '../../config/config';
import i18n from '../../i18n';
import {
  AnswerResponse,
  StreamEventType,
  ErrorResponse,
  DocumentRef,
  FeedbackCheckResponse,
  ConversationSummary,
  ConversationListResponse,
  ConversationTurn,
  ConversationTurnsResponse,
  UserFeedbackSubmission,
  SuggestedQuestionsResponse,
} from '../../types';

/**
 * Ask a question to the knowledge graph for a specific site.
 *
 * Conversation support:
 *  - Pass `new_conversation: true` (and omit `conversation_id`) to start a
 *    new server-side conversation. The response will include a `conversation_id`.
 *  - Pass `conversation_id` (and omit `new_conversation`) to continue an
 *    existing conversation, which includes prior chat history as context.
 *  - Omit both for a fully stateless, one-shot question.
 */
export async function askQuestion(
  siteId: string,
  question: string,
  orgId: string,
  options?: { conversation_id?: string; new_conversation?: boolean },
): Promise<AnswerResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ANSWER(siteId)}`;

  // Build request body - only include conversation fields when provided
  const body: Record<string, unknown> = { question, debug: true };
  if (options?.conversation_id) {
    body.conversation_id = options.conversation_id;
  } else if (options?.new_conversation) {
    body.new_conversation = true;
  }

  return apiCall<AnswerResponse>(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(body),
  });
}

/** Load ranked starter questions for empty chat (IMS auth). */
const SUGGESTION_SOURCES: ReadonlyArray<SuggestedQuestionsResponse['source']> = [
  'cache_hits',
  'recent',
  'mixed',
  'agent_card',
];

export async function fetchSuggestedQuestions(
  siteId: string,
  _orgId: string,
  limit = 5,
): Promise<SuggestedQuestionsResponse> {
  const qs = new URLSearchParams({ limit: String(limit) });
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUGGESTED_QUESTIONS(siteId)}?${qs}`;
  const headers: Record<string, string> = {};
  if (OAUTH_CONFIG.CLIENT_ID) {
    headers['x-ims-clientid'] = OAUTH_CONFIG.CLIENT_ID;
  }
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await authFetch(url, { method: 'GET', headers });
    if (!response.ok) {
      return { suggestions: [], source: 'recent' };
    }
    const data = (await response.json()) as SuggestedQuestionsResponse;
    const src = SUGGESTION_SOURCES.includes(data.source) ? data.source : 'recent';
    const raw = data.suggestions;
    if (!Array.isArray(raw)) {
      return { suggestions: [], source: src };
    }
    const suggestions = raw
      .map((row) => {
        const r = row as unknown as Record<string, unknown>;
        const query_id = String(r.query_id ?? r.queryId ?? '').trim();
        const question = String(r.question ?? '').trim();
        const hit = Number(r.hit_count ?? r.hitCount ?? 0);
        return {
          query_id,
          question,
          hit_count: Number.isFinite(hit) ? hit : 0,
        };
      })
      .filter((s) => s.question.length > 0);
    return { suggestions, source: src };
  } catch {
    return { suggestions: [], source: 'recent' };
  }
}

/**
 * Stream an answer via Server-Sent Events.
 *
 * Uses fetch + ReadableStream (not EventSource) because the endpoint is POST
 * with custom auth headers. Calls `onEvent` for each parsed SSE message.
 *
 * @returns An AbortController that can cancel the stream.
 */
export function askQuestionStream(
  siteId: string,
  question: string,
  orgId: string,
  options: { conversation_id?: string; new_conversation?: boolean } | undefined,
  onEvent: (eventType: StreamEventType, data: Record<string, any>) => void,
  onError: (error: Error) => void,
  onDone: () => void,
): AbortController {
  const controller = new AbortController();
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ANSWER_STREAM(siteId)}`;

  const body: Record<string, unknown> = { question, debug: true };
  if (options?.conversation_id) {
    body.conversation_id = options.conversation_id;
  } else if (options?.new_conversation) {
    body.new_conversation = true;
  }

  (async () => {
    try {
      const response = await authFetch(url, {
        method: 'POST',
        headers: getHeadersWithOrg(),
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        let msg = `Stream request failed: ${response.status}`;
        try {
          const errBody = await response.json();
          msg = errBody.detail || errBody.error || msg;
        } catch { /* ignore */ }
        throw new Error(msg);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error(i18n.t('errors.conversations.streamUnsupported'));

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE frames: "event: <type>\ndata: <json>\n\n"
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';

        for (const frame of frames) {
          if (!frame.trim()) continue;
          let eventType: StreamEventType = 'phase';
          let dataStr = '';

          for (const line of frame.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim() as StreamEventType;
            } else if (line.startsWith('data: ')) {
              dataStr = line.slice(6);
            }
          }

          if (dataStr) {
            try {
              const data = JSON.parse(dataStr);
              onEvent(eventType, data);
            } catch {
              // skip malformed JSON
            }
          }
        }
      }

      onDone();
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return controller;
}

/**
 * Evaluate the quality of a generated answer using LLM-as-Judge.
 *
 * This is a decoupled API call that should be fired asynchronously after
 * receiving an answer, so it does not block the chat flow.
 * The service uses the document GUIDs to fetch full content from Neo4j.
 */
export async function checkAnswerQuality(
  siteId: string,
  question: string,
  answer: string,
  orgId: string,
  finalDocumentList: DocumentRef[],
  conversationId: string,
  turnNumber: number,
): Promise<FeedbackCheckResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.FEEDBACK_CHECK(siteId)}`;

  const body: Record<string, unknown> = {
    question,
    answer,
    final_document_list: finalDocumentList,
    conversation_id: conversationId,
    turn_number: turnNumber,
  };

  return apiCall<FeedbackCheckResponse>(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(body),
  });
}

/**
 * Submit user feedback for a specific conversation turn.
 *
 * Creates a UserFeedback node in the knowledge graph linked to the
 * conversation turn and the referenced DITA documents.
 */
export async function submitUserFeedback(
  siteId: string,
  orgId: string,
  conversationId: string,
  turnNumber: number,
  finalDocumentList: DocumentRef[],
  feedback: UserFeedbackSubmission,
): Promise<{ success: boolean }> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.FEEDBACK(siteId)}`;

  const body = {
    conversation_id: conversationId,
    turn_number: turnNumber,
    final_document_list: finalDocumentList,
    relevance: feedback.relevance,
    completeness: feedback.completeness,
    faithfulness: feedback.faithfulness,
    coherence: feedback.coherence,
  };

  return apiCall<{ success: boolean }>(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(body),
  });
}

// =============================================================================
// Conversation History API Functions
// =============================================================================

/**
 * Fetch the most recent conversations for the current user on a site.
 * Returns conversation summaries (id, first question, timestamp, turn count).
 */
export async function fetchRecentConversations(
  siteId: string,
  orgId: string,
  limit: number = 5,
): Promise<ConversationSummary[]> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSATIONS(siteId)}?limit=${limit}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.error || `Failed to fetch conversations: ${response.status}`);
    }

    const data: ConversationListResponse = await response.json();
    return data.conversations || [];
  } catch (error) {
    if (error instanceof Error) {
      console.warn(`Failed to fetch conversations for site ${siteId}:`, error.message);
      return [];
    }
    return [];
  }
}

/**
 * Fetch all turns for a specific conversation.
 * Used to restore a past conversation into the chat view.
 */
export async function fetchConversationTurns(
  siteId: string,
  conversationId: string,
  orgId: string,
): Promise<ConversationTurn[]> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSATION_TURNS(siteId, conversationId)}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.error || `Failed to fetch conversation turns: ${response.status}`);
    }

    const data: ConversationTurnsResponse = await response.json();
    return data.turns || [];
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.conversations.turnsFetchFailed'));
  }
}
